import { A11yModule } from '@angular/cdk/a11y';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isNil } from 'lodash';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  filter,
  finalize,
  map,
  Observable,
  of,
  share,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CuttingOrderQuestion } from '../../models/cutting/cutting-order-question.model';
import { IPaginatedList } from '../../models/paginated-list.model';
import { DynamicFormsModule } from '../../modules/dynamic-forms/dynamic-forms.module';
import { OverlayLoaderModule } from '../../modules/overlay-loader/overlay-loader.module';
import { PipesModule } from '../../pipes/pipes.module';

import { HttpErrorResponse } from '@angular/common/http';
import { ChatAttachment, ChatMessage, ChatMessageResponse, NewMessage } from 'cloud-shared-lib';
import { SupportMessageComponent } from './support-chat-message/support-chat-message.component';
import { SupportChatService } from './support-chat.service';

@Component({
  selector: 'csl-support-chat',
  templateUrl: './support-chat.component.html',
  styleUrls: ['./support-chat.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    QuillEditorComponent,
    PipesModule,
    CommonModule,
    FormsModule,
    SupportMessageComponent,
    AsyncPipe,
    NgIf,
    MatChipsModule,
    MatLegacyProgressSpinnerModule,
    MatIconModule,
    A11yModule,
    MatLegacyButtonModule,
    OverlayLoaderModule,
    DynamicFormsModule,
  ],
})
export class SupportChatComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('messageContainer') private messageContainer: ElementRef;
  @ViewChild('quillEditor') quillEditor: QuillEditorComponent;
  @ViewChild('supportElement') supportElement: ElementRef;

  @Input() actionButtons: { title: string; action: () => void }[] = [];
  @Input() myId: string;
  @Input() questionId: number;
  @Input() handleFileUploadError: (error: HttpErrorResponse) => void;
  @Input() question: CuttingOrderQuestion;
  @Output() messageSent = new EventEmitter<ChatMessage>();

  readonly maxMessageLength = 600;

  messages: ChatMessage[] = [];
  files: File[] = [];
  pageIndex = 0;
  isFirstLoad = true;
  messageText = '';
  attachments: ChatAttachment[] = [];
  lastScrollHeight = 0;
  lastScrollTop = 0;
  isMobile: boolean;
  loadedAllMessages = false;
  totalMessagesCount = 0;
  remainingMessagesCount = 0;
  editorStyle: unknown = {};

  private isDraggingSubject = new BehaviorSubject<boolean>(false);
  isDragging$ = this.isDraggingSubject.asObservable().pipe(debounceTime(30));

  private unsubscribe$ = new Subject<void>();

  private allMessagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  allMessages$ = this.allMessagesSubject.asObservable();

  private loadAllMessages$ = new Subject<boolean>();

  quillModules: QuillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
        ['attach'],
      ],
      handlers: {
        attach: () => {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('multiple', '');
          input.style.display = 'none';

          input.addEventListener('change', (event: Event) => {
            const target = event.target as HTMLInputElement;
            if (target.files && target.files.length > 0) {
              for (let i = 0; i < target.files.length; i++) {
                const file = target.files[i];
                this.handleFile(file);
              }
            }
          });

          document.body.appendChild(input);
          input.click();
          document.body.removeChild(input);
        },
      },
    },
  };

  readonly messagesRequest$ = this.loadAllMessages$.pipe(
    startWith(false),
    switchMap(all => this.fetchMessages(all)),
    tap(data => this.processMessages(data)),
    share()
  );

  readonly loadingMessages$ = this.messagesRequest$.pipe(
    map(value => !value),
    startWith(true)
  );

  readonly total$ = this.messagesRequest$.pipe(
    filter(data => !isNil(data)),
    map((data: IPaginatedList<ChatMessage>) => data.totalCount),
    startWith(1)
  );

  readonly messages$ = this.messagesRequest$.pipe(
    filter(data => data !== null && data !== undefined),
    map((data: IPaginatedList<ChatMessage>) => data.items),
    tap(() => {
      if (this.isFirstLoad) {
        setTimeout(() => this.scrollToBottom(), 0);
        this.isFirstLoad = false;
      }
    }),
    startWith([])
  );

  constructor(
    private supportChatService: SupportChatService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private platform: Platform,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.isMobile = this.platform.ANDROID || this.platform.IOS;
    this.supportChatService.newMessagesSubject.pipe(takeUntil(this.unsubscribe$)).subscribe((message: ChatMessage) => {
      if (!this.messages.some(m => m.id === message.id && message.id !== 0)) {
        this.messages.unshift(message);
        this.allMessagesSubject.next(this.messages);
        this.markAsRead();
      }
    });

    this.supportChatService.newAttachmentsSubject
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((attachments: ChatAttachment[]) => {
        if (attachments.length > 0) {
          const messageId = attachments[0].messageId;
          const message = this.messages.find(x => x.id === messageId);

          if (message) {
            for (const item of attachments) {
              message.attachments.push(item);
            }
            this.markAsRead();
          }
        }
      });

    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => {
        this.setEditorStyles(result.matches);
      });

    if (this.question) {
      this.loadAllMessages$.next(false);
    }

    document.addEventListener('paste', this.onPasteFile.bind(this));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    document.removeEventListener('paste', this.onPasteFile.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.questionId && !changes.questionId.isFirstChange()) {
      this.resetState();
      this.loadAllMessages$.next(false);
    }
  }

  loadAllMessages(): void {
    this.saveScrollPosition();

    this.loadAllMessages$.next(true);
  }

  loadPreviousMessages(): void {
    this.loadAllMessages$.next(true);
  }

  setEditorStyles(isHandset: boolean): void {
    if (isHandset) {
      this.editorStyle = {
        'border-bottom-left-radius': '0.5rem',
        'border-bottom-right-radius': '0.5rem',
        'border-bottom': '1px solid #d1d5db',
        'min-height': '0px',
      };
    } else {
      this.editorStyle = {
        'border-bottom-left-radius': 'unset',
        'border-bottom-right-radius': 'unset',
        'border-bottom': 'none',
        'min-height': '100px',
      };
    }
  }

  markAsRead(): void {
    if (this.questionId == null) {
      return;
    }

    this.supportChatService
      .markAsRead(this.questionId)
      .pipe(
        tap(() => {
          this.messages.forEach(message => {
            message.read = true;
          });
          this.allMessagesSubject.next(this.messages);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  sendMessage(): void {
    if (!this.validateMessage()) {
      return;
    }

    const newMessage = this.prepareNewMessage();
    this.resetMessageInput();

    this.supportChatService
      .sendMessage(this.questionId, newMessage)
      .pipe(
        switchMap(this.handleSentMessage.bind(this)),
        catchError(this.handleSendError.bind(this)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  private validateMessage(): boolean {
    const text = this.quillEditor.quillEditor.getText().trim();

    if (!text.length) {
      this.snackBar.open($localize`Описание не может быть пустым`);
      this.messageText = '';
      return false;
    }

    const filesSize = this.files.reduce((total, file) => total + file.size, 0);
    if (filesSize / 1024 / 1024 > 60.0) {
      this.snackBar.open($localize`Максимальный объем файлов - 60MB`);
      return false;
    }

    if (this.messageText.length > this.maxMessageLength) {
      this.snackBar.open($localize`Превышено максимально возможное количество символов в сообщении`);
      return false;
    }

    return true;
  }

  private prepareNewMessage(): NewMessage {
    return {
      text: this.messageText.trim(),
      attachments: this.attachments,
    };
  }

  private resetMessageInput(): void {
    this.messageText = '';
    this.cdr.markForCheck();
  }

  private handleSentMessage(message: ChatMessage): Observable<ChatMessage | ChatAttachment[]> {
    const notifyMessage = structuredClone(message);
    notifyMessage.attachments = [];
    this.messageSent.emit(notifyMessage);

    this.updateMessageStatus(message.id, { isSending: false });
    this.allMessagesSubject.next(this.messages);
    setTimeout(() => this.scrollToBottom(), 50);
    this.cdr.markForCheck();

    if (this.files.length > 0) {
      return this.uploadFiles(message).pipe(
        map((attachments: ChatAttachment[]) => {
          if (attachments.length > 0) {
            const messageId = attachments[0].messageId;
            const message = this.messages.find(x => x.id === messageId);
            if (message) {
              for (const item of attachments) {
                if (!message.attachments.some(x => x.fileName === item.fileName)) {
                  message.attachments.push(item);
                }
              }
            }
          }
          return attachments;
        }),
        tap(() => {
          this.allMessagesSubject.next(this.messages);
        })
      );
    } else {
      return of([]);
    }
  }

  private uploadFiles(message: ChatMessage): Observable<ChatAttachment[]> {
    return this.supportChatService.uploadFiles(this.files, this.questionId, message.id).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.handleFileUploadError) {
          this.handleFileUploadError(error);
        } else {
          this.snackBar.open($localize`Ошибка отправки файлов`);
        }
        return of([]);
      }),
      finalize(() => {
        this.files = [];
        this.attachments = [];
        this.cdr.markForCheck();
      })
    );
  }

  private handleSendError(message: ChatMessage): Observable<null> {
    this.updateMessageStatus(message.id, { isSending: false, error: true });
    this.snackBar.open($localize`Ошибка отправки сообщения`);
    return of(null);
  }

  resetState(): void {
    this.messages = [];
    this.pageIndex = 0;
    this.isFirstLoad = true;
    this.messageText = '';
    this.question = null;
  }

  private fetchMessages(all: boolean): Observable<ChatMessageResponse> {
    if (this.questionId === null) {
      return of({ totalCount: 0, items: [] });
    }
    return this.supportChatService.getMessages(this.questionId, all).pipe(
      catchError(() => {
        return of({ totalCount: 0, items: [] });
      })
    );
  }

  private processMessages(data: ChatMessageResponse): void {
    this.sortMessages(data.items);
    this.updateMessagesList(data);
    this.updateMessageCounts(data);
    this.updateUIState();
    this.markAsRead();
  }

  private sortMessages(messages: ChatMessage[]): void {
    messages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  private updateMessagesList(data: ChatMessageResponse): void {
    const newMessages = data.items.filter(newMsg => !this.messages.some(existingMsg => existingMsg.id === newMsg.id));

    if (this.loadedAllMessages) {
      this.messages = data.items.reverse();
    } else {
      this.messages = [...newMessages.reverse(), ...this.messages];
    }

    this.allMessagesSubject.next(this.messages);
    this.loadedAllMessages = data.totalCount <= this.messages.length;
  }

  private updateMessageCounts(data: ChatMessageResponse): void {
    this.totalMessagesCount = data.totalCount;
    this.remainingMessagesCount = Math.max(0, this.totalMessagesCount - this.messages.length);
  }

  private updateUIState(): void {
    setTimeout(() => this.restoreScrollPosition(), 0);
    this.cdr.markForCheck();

    if (this.isFirstLoad) {
      setTimeout(() => this.scrollToBottom(), 100);
      this.isFirstLoad = false;
    }
  }

  restoreScrollPosition(): void {
    const container = this.messageContainer.nativeElement;
    const newScrollHeight = container.scrollHeight;
    container.scrollTop = newScrollHeight - (this.lastScrollHeight - this.lastScrollTop);
  }

  saveScrollPosition(): void {
    const container = this.messageContainer.nativeElement;
    this.lastScrollTop = container.scrollTop;
    this.lastScrollHeight = container.scrollHeight;
  }

  updateMessageStatus(messageId: number, updates: Partial<ChatMessage>): void {
    const messageIndex = this.messages.findIndex(msg => msg.id === messageId);
    if (messageIndex !== -1) {
      this.messages[messageIndex] = { ...this.messages[messageIndex], ...updates };
      this.allMessagesSubject.next(this.messages);
    }
  }

  private scrollToBottom(): void {
    const container = this.messageContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }

  onPasteFile(event: ClipboardEvent): void {
    if (!document.activeElement || !this.supportElement.nativeElement.contains(document.activeElement)) {
      return;
    }

    let isFile = false;

    if (event.clipboardData) {
      const items = event.clipboardData.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
              this.handleFile(file);
              isFile = true;
            }
          }
        }
        if (isFile) {
          event.stopPropagation();
          event.preventDefault();
        }
      }
    }
  }

  onGlobalDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingSubject.next(false);
    const dropZone = document.querySelector('.drop-zone');
    if (!dropZone || !dropZone.contains(event.target as Node)) {
      this.isDraggingSubject.next(false);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget || !relatedTarget.closest('.drop-zone')) {
      this.isDraggingSubject.next(false);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.types.includes('Files')) {
      this.isDraggingSubject.next(true);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const dropZone = document.querySelector('.drop-zone');

    if (!dropZone || !dropZone.contains(event.target as Node)) {
      this.isDraggingSubject.next(false);
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        this.handleFile(files[i]);
      }
    }

    this.isDraggingSubject.next(false);
  }

  handleFile(file: File): void {
    this.files.push(file);
    const attachment: ChatAttachment = {
      id: 0,
      fileName: file.name,
      messageId: 0,
      fileSize: file.size,
    };

    this.attachments.push(attachment);

    this.cdr.markForCheck();
  }

  removeFile(index: number): void {
    this.attachments.splice(index, 1);
    this.files.splice(index, 1);
  }
}
